import { useState, type ReactNode } from 'react';
import { Building2, ChevronsUpDown, Check, Globe, Plus, Loader2 } from 'lucide-react';
import { useOrg } from '../auth/OrgContext';
import { cx } from './ui';

// Top-of-menu Org switcher (PRD §2.2/§2.9): the active Org, or "All Orgs" (tenant-wide / global view).
function Item({
  label,
  active,
  icon,
  onClick,
}: {
  label: string;
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cx(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-ink/[0.04]',
        active ? 'text-ink' : 'text-muted',
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {active && <Check className="h-4 w-4 shrink-0 text-ink" />}
    </button>
  );
}

export function OrgSwitcher() {
  const { orgs, tenant, selectedOrgId, selectedOrg, isMaster, setOrg, createOrg } = useOrg();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const title = selectedOrg ? selectedOrg.name : (tenant?.name ?? 'All Orgs');
  const sub = selectedOrg ? 'Org' : isMaster ? 'All orgs · global' : 'All orgs';

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const org = await createOrg(name.trim());
      setOrg(org.id);
      setName('');
      setAdding(false);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative px-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-lg border border-line px-2.5 py-2 text-left transition-colors hover:border-ink/30"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-ink text-paper">
          {selectedOrg ? <Building2 className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{title}</span>
          <span className="block truncate text-xs text-muted">{sub}</span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-3 right-3 z-20 mt-1 max-h-80 overflow-auto rounded-lg border border-line bg-paper py-1 shadow-card">
            <Item
              label={isMaster ? 'All Orgs · Global' : 'All Orgs'}
              active={!selectedOrgId}
              icon={<Globe className="h-4 w-4" />}
              onClick={() => {
                setOrg(null);
                setOpen(false);
              }}
            />
            {orgs.length > 0 && <div className="my-1 border-t border-line" />}
            {orgs.map((o) => (
              <Item
                key={o.id}
                label={o.name}
                active={selectedOrgId === o.id}
                icon={<Building2 className="h-4 w-4" />}
                onClick={() => {
                  setOrg(o.id);
                  setOpen(false);
                }}
              />
            ))}
            {orgs.length === 0 && <div className="px-3 py-2 text-xs text-muted">No orgs yet.</div>}

            <div className="my-1 border-t border-line" />
            {adding ? (
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void add()}
                  placeholder="Organization name"
                  autoFocus
                  className="min-w-0 flex-1 rounded-input border border-line px-2 py-1.5 text-sm text-ink focus:border-ink focus:outline-none"
                />
                <button
                  onClick={() => void add()}
                  disabled={busy || !name.trim()}
                  className="shrink-0 rounded-md bg-ink px-2.5 py-1.5 text-xs font-medium text-paper disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span className="flex-1">Add Organization</span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
